import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  ScrollArea,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@aaas/shared';
import type { CV } from '../../types';
import { useMemo } from 'react';

// Status badge component
function ProcessingStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('ats');

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'processed':
        return { label: t('cvSelection.status.processed'), color: 'bg-green-100 text-green-700' };
      case 'processing':
        return { label: t('cvSelection.status.processing'), color: 'bg-blue-100 text-blue-700' };
      case 'pending':
        return { label: t('cvSelection.status.pending'), color: 'bg-yellow-100 text-yellow-700' };
      case 'failed':
        return { label: t('cvSelection.status.failed'), color: 'bg-red-100 text-red-700' };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-700' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.label}
    </span>
  );
}

interface CVSelectionTableProps {
  cvs: CV[];
  selectedCVIds: Set<string>;
  onToggleSelect: (cvId: string) => void;
  isLoading: boolean;
  onPreviewCV?: (cv: CV) => void;
}

export function CVSelectionTable({
  cvs,
  selectedCVIds,
  onToggleSelect,
  isLoading,
  onPreviewCV,
}: CVSelectionTableProps) {
  const { t } = useTranslation('ats');

  // Select all logic
  const allSelected = useMemo(() => cvs.length > 0 && cvs.every((cv) => selectedCVIds.has(cv.id)), [cvs, selectedCVIds]);

  const toggleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      cvs.forEach((cv) => {
        if (selectedCVIds.has(cv.id)) onToggleSelect(cv.id);
      });
    } else {
      // Select all
      cvs.forEach((cv) => {
        if (!selectedCVIds.has(cv.id)) onToggleSelect(cv.id);
      });
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-orange-600 mx-auto mb-2 sm:mb-3"></div>
        <p className="text-xs sm:text-sm text-gray-500">{t('cvSelection.loadingCvs')}</p>
      </div>
    );
  }

  if (cvs.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 text-gray-500">
        <p className="text-xs sm:text-sm">{t('cvSelection.noCvsInPools')}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <ScrollArea className="h-[294px]">
        {/* Header Table - Fixed */}
        <Table className="min-w-[640px]">
          <TableHeader className="bg-gray-50 border-b">
            <TableRow className="hover:bg-gray-50">
              <TableHead className="w-10 sm:w-12 px-2 sm:px-4 py-3 bg-gray-50 ">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  disabled={cvs.length === 0}
                  className="h-5 w-5"
                />
              </TableHead>
              <TableHead className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-700 bg-gray-50">
                {t('cvSelection.columns.name')}
              </TableHead>
              <TableHead className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-700 bg-gray-50 hidden sm:table-cell">
                {t('cvSelection.columns.job')}
              </TableHead>
              <TableHead className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-700 bg-gray-50 hidden md:table-cell">
                {t('cvSelection.columns.pool')}
              </TableHead>
              <TableHead className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-medium text-gray-700 bg-gray-50">
                {t('cvSelection.columns.status')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-gray-200">
              {cvs.map((cv) => {
                const isSelected = selectedCVIds.has(cv.id);

                return (
                  <TableRow
                    key={cv.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <TableCell className="w-10 sm:w-12 px-2 sm:px-4 py-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelect(cv.id)}
                        className="h-5 w-5"
                      />
                    </TableCell>
                    <TableCell className="px-2 sm:px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {onPreviewCV && cv.attachments.length > 0 ? (
                          <button
                            onClick={() => onPreviewCV(cv)}
                            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 hover:underline break-all text-left"
                          >
                            {cv.name}
                          </button>
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-900 break-all">
                            {cv.name}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 sm:px-4 py-3 hidden sm:table-cell">
                      {cv.jobs && cv.jobs.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {cv.jobs.map((job) => (
                            <span
                              key={job.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700"
                              title={`${job.position_name} - ${job.campaign_name}`}
                            >
                              {job.code}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs sm:text-sm text-gray-400 italic">{t('cvSelection.noJob')}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-2 sm:px-4 py-3 hidden md:table-cell">
                      {cv.pools && cv.pools.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {cv.pools.map((pool) => (
                            <span
                              key={pool.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700"
                            >
                              {pool.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs sm:text-sm text-gray-400 italic">{t('cvSelection.noPool')}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-2 sm:px-4 py-3">
                      <ProcessingStatusBadge status={cv.processing_status} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
